import { useEffect, useRef } from "react";

declare global {
  interface Window {
    zE?: (...args: unknown[]) => void;
    zESettings?: Record<string, unknown>;
  }
}

interface ZendeskProps {
  zendeskKey: string;
  defer?: boolean;
  onLoaded?: () => void;
  [key: string]: unknown;
}

export const ZendeskAPI = (...args: unknown[]) => {
  if (typeof window !== "undefined" && window.zE) {
    window.zE(...args);
  } else {
    console.warn("Zendesk is not initialized yet");
  }
};

export const Zendesk = ({ zendeskKey, defer, onLoaded, ...other }: ZendeskProps) => {
  const onLoadedRef = useRef(onLoaded);
  const settingsRef = useRef(other);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  useEffect(() => {
    if (typeof window === "undefined" || window.zE) {
      return;
    }

    const script = document.createElement("script");
    script.id = "ze-snippet";
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;

    if (defer) {
      script.defer = true;
    } else {
      script.async = true;
    }

    const handleLoad = () => {
      onLoadedRef.current?.();
    };

    script.addEventListener("load", handleLoad);

    window.zESettings = settingsRef.current;
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      const existingScript = document.getElementById("ze-snippet");
      if (existingScript) {
        existingScript.remove();
      }
      delete window.zE;
      delete window.zESettings;
    };
  }, [zendeskKey, defer]);

  return null;
};

export default Zendesk;
